import { useEffect, useRef, useState } from "react";
import { Plus, Star } from "lucide-react";

import ButtonV2 from "../../../../components/ui/button/v2";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";

import { INLINE_ROW_LIMIT, LoanAccount, PrimaryParty, buildPartyTypeOptions } from "../../../../api/creditAccounts";
import { buildEmptyParty, buildEmptyAccountRelations } from "../../utils/creditFactories";
import { PartySuggestion, SectionIssue } from "./derive";
import { SectionAccordion, CollectionModal, ViewMoreButton } from "./SectionShell";
import PartyRow from "./PartyRow";
import { v4 as uuidv4 } from "uuid";
import { SuggestionCard } from "./SuggestionCard";

interface PartiesSectionProps {
  parties: PrimaryParty[];
  onPartiesChange: (next: PrimaryParty[]) => void;
  loanAccounts: LoanAccount[];
  suggestions: PartySuggestion[];
  showErrors: boolean;
  issue: SectionIssue;
  focus: { key: string; seq: number };
}

const PartiesSection: React.FC<PartiesSectionProps> = ({
  parties,
  onPartiesChange,
  loanAccounts,
  suggestions,
  showErrors,
  issue,
  focus,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const accountOptions: OptionNameValuePair<string>[] = loanAccounts
    .filter((account) => account.extAccountNumber.trim())
    .map((account) => ({
      name: account.extAccountNumber.trim(),
      value: account.accountNumber,
    }));
  const addEnabled = issue.incompleteRows === 0;

  useEffect(() => {
    if (focus.key !== "primaryParties") return;
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (issue.hiddenInvalid > 0) setModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus.seq]);

  const add = () => {
    const next = [...parties, buildEmptyParty()];
    onPartiesChange(next);
    if (next.length > INLINE_ROW_LIMIT) setModalOpen(true);
  };

  const onPartyChange = (updated: PrimaryParty) => {
    onPartiesChange(parties.map((p) => (p.id === updated.id ? updated : p)));
  }
    
  const remove = (id: string) => onPartiesChange(parties.filter((p) => p.id !== id));

  const addSuggestion = (s: PartySuggestion) => {
    const existingParty = parties.find((party) => party.name === s.name);

    if(existingParty) {
      const updated:PrimaryParty = {
        ...existingParty,
        account_relationships: [
          ...s.account_relationships.map(relationship => ({id: uuidv4(), ...relationship})),
          ...existingParty.account_relationships
        ]
      }

      onPartyChange(updated);
    } else {
      const newParty: PrimaryParty = {
        ...buildEmptyParty(),
        name: s.name,
        account_relationships: s.account_relationships.map((rs) => ({
          ...buildEmptyAccountRelations(),
          related_as: rs.related_as,
          account_numbers: rs.account_numbers,
        })),
      }

      onPartiesChange([
        ...parties,
        newParty,
      ]);
    }
  }
    
  const renderRows = (list: PrimaryParty[]) => (
    <div className="flex flex-col gap-4">
      {list.map((party) => (
        <PartyRow key={party.id} party={party} all={parties}
          accountOptions={accountOptions}
          showErrors={showErrors} onChange={onPartyChange} onDelete={() => remove(party.id)} />
      ))}
    </div>
  );

  const addButton = (
    <ButtonV2 variant="ghost" size="sm" disabled={!addEnabled} onClick={add}
      className="text-[#3551F3] bg-transparent self-start disabled:opacity-40"
      prefix={<Plus width="16" height="16" />}>
      Add Primary Party
    </ButtonV2>
  );

  const suggestionsBlock = suggestions.length > 0 && (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-xs font-medium text-gray-600">
        <span className="flex items-center gap-1">
          <Star width={14} height={14} className="text-[#3551F3]" /> Suggestions
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((s) => (
          <SuggestionCard key={s.name} suggestion={s} onSelect={() => addSuggestion(s)} />
        ))}
      </div>
    </div>
  );

  return (
    <>
      <div ref={rootRef}>
        <SectionAccordion title="Primary Parties" count={parties.length}
          invalidCount={showErrors ? issue.incompleteRows : 0} onExpand={() => setModalOpen(true)}>
          <div className="flex flex-col gap-4">
            {renderRows(parties.slice(0, INLINE_ROW_LIMIT))}
            <div className="flex items-center justify-between">
              {addButton}
              <ViewMoreButton overflowCount={parties.length - INLINE_ROW_LIMIT}
                singular="party" plural="parties"
                invalidCount={showErrors ? issue.hiddenInvalid : 0}
                onClick={() => setModalOpen(true)} />
            </div>
            {suggestionsBlock}
          </div>
        </SectionAccordion>
      </div>

      {modalOpen && (
        <CollectionModal title="Primary Parties" onClose={() => setModalOpen(false)} onDone={() => setModalOpen(false)}>
          <div className="flex flex-col gap-4">
            {renderRows(parties)}
            {addButton}
            {suggestionsBlock}
          </div>
        </CollectionModal>
      )}
    </>
  );
};

export default PartiesSection;
